import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import {
  CreateDaywiseItinerary,
  GetDaywiseItineraryByHoliday,
  GetDaywiseItineraryById,
  GetHolidays,
  GetSightseeingList,
  UpdateDaywiseItinerary,
  GenerateHolidayItineraryAI,
} from '../../entities/action/action';
import DayEditor from './DayEditor';
import HotelCatalogue from './HotelCatalogue';
import TransportCatalogue from './TransportCatalogue';
import PricingSummary from './PricingSummary';
import BookingSimulator from './BookingSimulator';
import {
  buildDaywiseCreatePayload,
  mapItineraryApiToEditor,
  normalizeDaywiseImport,
  parseDaywiseJsonText,
} from './daywisePayload';
import dw from './daywiseItinerary.module.css';
import { normalizeSightseeingList } from './sightseeingCatalogue';

const createEmptyDay = (dayNumber) => ({
  dayNumber,
  title: `Day ${dayNumber}`,
  city: '',
  description: '',
  legId: dayNumber > 1 ? `leg${dayNumber - 1}` : '',
  overnightCity: '',
  services: [],
});

const initialData = () => ({
  holidayId: '',
  itineraryName: '',
  route: '',
  totalDays: 1,
  totalNights: 0,
  markupPercent: 0,
  gstRate: 5,
  gstMode: 'exclusive',
  flightsIncluded: false,
  flightsNote: '',
  days: [createEmptyDay(1)],
  hotels: [],
  transports: [],
});

const inferDestinationFromRoute = (route) => {
  if (!route || typeof route !== 'string') return '';
  const parts = route.split(/→|->|>/).map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : route.trim();
};

const MODULE_NAV = [
  { key: 'hotels', title: 'Hotels & room rates', hint: 'Per room / per person', icon: '★' },
  { key: 'transport', title: 'Transport catalogue', hint: 'Journey & full-tour', icon: '↗' },
  { key: 'booking', title: 'Customer room allocation', hint: 'Manual room selection', icon: '▦' },
  { key: 'tax', title: 'GST & publish', hint: 'Final quote & tax', icon: '₹' },
];

const DaywiseItineraryEditor = ({ editItineraryId, editItineraryNonce }) => {
  const [view, setView] = useState('day');
  const [currentDay, setCurrentDay] = useState(1);
  const [data, setData] = useState(initialData());
  const [holidays, setHolidays] = useState([]);
  const [sightseeings, setSightseeings] = useState([]);
  const [sightseeingsLoadHint, setSightseeingsLoadHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingItinerary, setLoadingItinerary] = useState(false);
  const [aiJson, setAiJson] = useState('');
  const [aiFrom, setAiFrom] = useState('');
  const [aiBrief, setAiBrief] = useState('');
  const [aiBudget, setAiBudget] = useState('Standard');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [travellers, setTravellers] = useState({
    indianAdults: 2,
    foreignAdults: 0,
    childrenWithBed: 0,
    childrenNoBed: 0,
  });
  const [bookingState, setBookingState] = useState({
    hotelSelection: {},
    roomAllocation: {},
    transportPackageId: null,
  });

  useEffect(() => {
    (async () => {
      const [holidayRes, sightseeingRes] = await Promise.all([
        GetHolidays(),
        GetSightseeingList(),
      ]);
      if (holidayRes?.success) {
        setHolidays(holidayRes?.holidays || []);
      }
      if (sightseeingRes?.success) {
        const visible = (sightseeingRes?.sightSeeings ?? []).filter(
          (s) => s && s.isVisible !== false && s.visible !== false
        );
        const items = normalizeSightseeingList(visible);
        setSightseeings(items);
        setSightseeingsLoadHint(
          items.length
            ? ''
            : 'Sightseeing catalogue is empty — add entries under Sightseeing Management.'
        );
      } else {
        setSightseeings([]);
        setSightseeingsLoadHint(
          sightseeingRes?.message || 'Could not load sightseeing catalogue.'
        );
      }
    })();
  }, []);

  const cities = useMemo(() => {
    const set = new Set();
    (data.days || []).forEach((d) => {
      if (d.overnightCity) set.add(d.overnightCity);
      else if (d.city) set.add(d.city);
    });
    return [...set];
  }, [data.days]);

  const syncDayCount = (totalDays) => {
    const n = Math.max(1, Number(totalDays) || 1);
    setData((prev) => {
      let days = [...(prev.days || [])];
      while (days.length < n) {
        days.push(createEmptyDay(days.length + 1));
      }
      while (days.length > n) {
        days.pop();
      }
      days = days.map((d, i) => ({ ...d, dayNumber: i + 1 }));
      return {
        ...prev,
        totalDays: n,
        totalNights: Math.max(0, n - 1),
        days,
      };
    });
    if (currentDay > n) setCurrentDay(n);
  };

  const patchMeta = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const loadItineraryById = async (id) => {
    if (!id) return;
    setLoadingItinerary(true);
    try {
      const res = await GetDaywiseItineraryById(id);
      if (res?.success && res?.itinerary) {
        const mapped = mapItineraryApiToEditor(res.itinerary);
        if (mapped) {
          setData(mapped);
          setCurrentDay(1);
          setView('day');
          return;
        }
      }
      alert(res?.message || 'Could not load itinerary.');
    } catch (e) {
      alert('Could not load itinerary.');
    } finally {
      setLoadingItinerary(false);
    }
  };

  useEffect(() => {
    if (!editItineraryId) return;
    loadItineraryById(editItineraryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItineraryId, editItineraryNonce]);

  const loadItineraryForHoliday = async (holidayId) => {
    if (!holidayId) {
      setData(initialData());
      setCurrentDay(1);
      return;
    }
    setLoadingItinerary(true);
    try {
      const res = await GetDaywiseItineraryByHoliday(holidayId);
      let itinerary = res?.itinerary;
      if (!itinerary && Array.isArray(res?.itineraries)) {
        const row = res.itineraries
          .filter((i) => String(i.holidayId) === String(holidayId))
          .sort((a, b) => (b.id || 0) - (a.id || 0))[0];
        if (row?.id) {
          const one = await GetDaywiseItineraryById(row.id);
          if (one?.success && one?.itinerary) {
            itinerary = one.itinerary;
          }
        }
      }
      if (res?.success && itinerary) {
        const mapped = mapItineraryApiToEditor(itinerary);
        if (mapped) {
          setData(mapped);
          setCurrentDay(1);
          return;
        }
      }
      setData({ ...initialData(), holidayId: String(holidayId) });
      setCurrentDay(1);
    } catch (e) {
      alert('Could not load itinerary for this holiday.');
    } finally {
      setLoadingItinerary(false);
    }
  };

  const dayIndex = currentDay - 1;
  const day = data.days?.[dayIndex];

  const updateDay = (nextDay) => {
    setData((prev) => {
      const days = [...prev.days];
      days[dayIndex] = nextDay;
      return { ...prev, days };
    });
  };

  const legTransportsForDay = (d) =>
    (data.transports || []).filter((t) => t.context === 'leg' && t.contextId === d?.legId);

  const localTransportsForDay = (d) =>
    (data.transports || []).filter(
      (t) =>
        t.context === 'local' &&
        t.dayNumber === d?.dayNumber &&
        t.contextId === String(d?.dayNumber)
    );

  const applyImportJson = (raw) => {
    const parsed = normalizeDaywiseImport(raw);
    setData((prev) => ({
      ...prev,
      ...parsed,
      holidayId: parsed.holidayId || prev.holidayId,
      days: parsed.days || prev.days,
      hotels: parsed.hotels || prev.hotels,
      transports: parsed.transports || prev.transports,
    }));
  };

  const handleLoadJsonIntoEditor = () => {
    try {
      applyImportJson(parseDaywiseJsonText(aiJson));
      alert('Imported into editor. Link holiday id if needed, then Save.');
    } catch (err) {
      alert(err.message || 'Invalid JSON');
    }
  };

  const handleGenerateWithAi = async () => {
    const destination =
      inferDestinationFromRoute(data.route) || data.itineraryName?.trim() || '';
    const promptText = aiBrief.trim();
    if (!destination && !promptText) {
      alert('Set route or itinerary name, or describe the trip in the AI brief.');
      return;
    }
    setAiGenerating(true);
    try {
      const payload = {
        destination,
        from: aiFrom.trim(),
        days: String(data.totalDays || 5),
        month: '',
        theme: '',
        adults: '2',
        children: '0',
        infants: '0',
        budget: aiBudget,
        citizen: 'Indian',
      };
      if (promptText) {
        payload.prompt = promptText;
      }
      const res = await GenerateHolidayItineraryAI(payload);
      if (res?.success && res?.aiResponse) {
        setAiJson(JSON.stringify(res.aiResponse, null, 2));
        applyImportJson(res.aiResponse);
        alert('AI draft loaded into editor. Review rates, select holiday, then Save.');
      } else {
        alert(res?.message || 'AI generation failed. Check Grok keys and ai_prompts in DB.');
      }
    } catch (e) {
      alert(e?.message || 'AI generation failed.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildDaywiseCreatePayload(data);
      if (!payload.holidayId) {
        alert('Select a holiday before saving.');
        setSaving(false);
        return;
      }
      const res = data.id
        ? await UpdateDaywiseItinerary(data.id, payload)
        : await CreateDaywiseItinerary(payload);
      if (res?.success) {
        const saved = mapItineraryApiToEditor(res.itinerary);
        if (saved) {
          setData(saved);
        } else if (res.itinerary?.id) {
          setData((prev) => ({ ...prev, id: res.itinerary.id }));
        }
        alert('Day-wise itinerary saved.');
      } else {
        alert(res?.message || 'Save failed. Check API response.');
      }
    } catch (e) {
      alert(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const goDay = (dayNumber) => {
    setView('day');
    setCurrentDay(dayNumber);
  };

  const mainSectionTitle = {
    day: day ? `Day ${day.dayNumber} · ${day.title || 'Untitled'}` : 'Day editor',
    hotels: 'Hotel & room rate catalogue',
    transport: 'Transport catalogue',
    booking: 'Customer booking & room allocation',
    tax: 'GST & pricing summary',
  };

  const mainSectionSubtitle = {
    day: 'Daily details and costs linked to this itinerary day',
    hotels: 'Configure hotels, room types, and nightly supplier rates',
    transport: 'Intercity segments, local vehicles, and full-tour packages',
    booking: 'Preview allocations and validate selections',
    tax: 'Markup, GST mode, and live totals from the booking panel',
  };

  return (
    <div className={dw.root}>
      <div className={dw.headerline}>
        <div>
          <p className={dw.eyebrow}>Admin · Itinerary pricing engine</p>
          <h3>Day-wise Itinerary & Dynamic Pricing</h3>
          <p className={dw.subtitle}>
            Edit itinerary days, service rates, hotel policies, and preview a customer room mix.
          </p>
        </div>
        <div className={dw.topActions}>
          <span className={dw.tagGreen}>● Editor</span>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save itinerary'}
          </Button>
        </div>
      </div>

      <div className={`${dw.card} ${dw.intro}`}>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Itinerary name</label>
          <Form.Control
            value={data.itineraryName}
            onChange={(e) => patchMeta({ itineraryName: e.target.value })}
          />
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Holiday</label>
          <Form.Control
            as="select"
            value={data.holidayId}
            disabled={loadingItinerary}
            onChange={(e) => loadItineraryForHoliday(e.target.value)}
          >
            <option value="">-- select --</option>
            {holidays.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Form.Control>
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Route</label>
          <Form.Control
            value={data.route}
            onChange={(e) => patchMeta({ route: e.target.value })}
            placeholder="Delhi → Jaipur → …"
          />
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Duration</label>
          <div className={dw.fieldReadout}>
            {data.totalDays} days / {data.totalNights} nights
          </div>
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Days (edit count)</label>
          <Form.Control
            type="number"
            min={1}
            value={data.totalDays}
            onChange={(e) => syncDayCount(e.target.value)}
          />
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Markup %</label>
          <Form.Control
            type="number"
            value={data.markupPercent}
            onChange={(e) => patchMeta({ markupPercent: Number(e.target.value) })}
          />
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>GST %</label>
          <Form.Control
            type="number"
            value={data.gstRate}
            onChange={(e) => patchMeta({ gstRate: Number(e.target.value) })}
          />
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>GST mode</label>
          <Form.Control
            as="select"
            value={data.gstMode}
            onChange={(e) => patchMeta({ gstMode: e.target.value })}
          >
            <option value="exclusive">Add GST on top</option>
            <option value="inclusive">GST included</option>
          </Form.Control>
        </div>
        <div className={dw.field}>
          <label className={dw.fieldLabel}>Flights</label>
          <Form.Check
            type="checkbox"
            label="Included in package"
            checked={data.flightsIncluded}
            onChange={(e) => patchMeta({ flightsIncluded: e.target.checked })}
          />
        </div>
        {data.flightsIncluded && (
          <div className={dw.field} style={{ gridColumn: '1 / -1' }}>
            <label className={dw.fieldLabel}>Flights note</label>
            <Form.Control
              value={data.flightsNote}
              onChange={(e) => patchMeta({ flightsNote: e.target.value })}
            />
          </div>
        )}
      </div>

      <fieldset className={`${dw.card} ${dw.importBlock}`}>
        <legend>Import AI / JSON (daywise schema)</legend>
        <Row className="mb-2 g-2">
          <Col md={3}>
            <label className={dw.fieldLabel}>Traveller origin (for AI)</label>
            <Form.Control
              value={aiFrom}
              onChange={(e) => setAiFrom(e.target.value)}
              placeholder="Delhi"
            />
          </Col>
          <Col md={3}>
            <label className={dw.fieldLabel}>Budget tier</label>
            <Form.Control
              as="select"
              value={aiBudget}
              onChange={(e) => setAiBudget(e.target.value)}
            >
              <option value="Economy">Economy</option>
              <option value="Standard">Standard</option>
              <option value="Premium">Premium</option>
            </Form.Control>
          </Col>
          <Col md={6}>
            <label className={dw.fieldLabel}>AI trip brief (optional)</label>
            <Form.Control
              value={aiBrief}
              onChange={(e) => setAiBrief(e.target.value)}
              placeholder="Honeymoon in Goa, 5 days, beach + spice plantation…"
            />
          </Col>
        </Row>
        <Button
          size="sm"
          variant="primary"
          className="mb-2 me-2"
          disabled={aiGenerating}
          onClick={handleGenerateWithAi}
        >
          {aiGenerating ? 'Generating…' : 'Generate with AI'}
        </Button>
        <small className="text-muted d-block mb-2">
          Uses route / itinerary name and duration above. Requires Grok + Unsplash keys and{' '}
          <code>ai_prompts.holiday_itinerary</code> (see scripts/holiday-itinerary-ai-prompt.txt).
        </small>
        <Form.Control
          as="textarea"
          rows={4}
          className={dw.importTextarea}
          value={aiJson}
          onChange={(e) => setAiJson(e.target.value)}
          placeholder='Paste JSON with days[], hotels[], transports[] — not legacy daysRequests'
        />
        <Button
          size="sm"
          variant="outline-primary"
          className="mt-2"
          onClick={handleLoadJsonIntoEditor}
        >
          Load into editor
        </Button>
      </fieldset>

      <div className={dw.content}>
        <aside className={`${dw.card} ${dw.nav}`}>
          <div className={dw.navHeading}>Itinerary days</div>
          {(data.days || []).map((d) => {
            const active = view === 'day' && currentDay === d.dayNumber;
            const overnight = d.overnightCity || d.city;
            return (
              <button
                key={d.dayNumber}
                type="button"
                className={active ? dw.navButtonActive : dw.navButton}
                onClick={() => goDay(d.dayNumber)}
              >
                <span className={active ? dw.navNumActive : dw.navNum}>{d.dayNumber}</span>
                <span className={dw.navButtonText}>
                  <b>{d.title || `Day ${d.dayNumber}`}</b>
                  <small>
                    {overnight ? `Overnight · ${overnight}` : 'Departure · no hotel'}
                  </small>
                </span>
              </button>
            );
          })}
          <div className={dw.navDivider} />
          <div className={dw.navHeading}>Pricing modules</div>
          {MODULE_NAV.map(({ key, title, hint, icon }) => {
            const active = view === key;
            return (
              <button
                key={key}
                type="button"
                className={active ? dw.navButtonActive : dw.navButton}
                onClick={() => setView(key)}
              >
                <span className={active ? dw.navNumActive : dw.navNum}>{icon}</span>
                <span className={dw.navButtonText}>
                  <b>{title}</b>
                  <small>{hint}</small>
                </span>
              </button>
            );
          })}
        </aside>

        <div className={dw.mainColumn}>
          <div className={`${dw.card} ${dw.mainCard}`}>
          <div className={dw.sectionTop}>
            <div>
              <small>Itinerary pricing</small>
              <h4>{mainSectionTitle[view]}</h4>
              <small>{mainSectionSubtitle[view]}</small>
            </div>
            <span className={dw.tag}>Day-wise rate manager</span>
          </div>

          {view === 'day' && (
            <DayEditor
              day={day}
              dayIndex={dayIndex}
              totalDays={data.totalDays}
              allDays={data.days}
              legTransports={legTransportsForDay(day)}
              localTransports={localTransportsForDay(day)}
              allTransports={data.transports}
              onChange={updateDay}
              onPrev={() => setCurrentDay((c) => Math.max(1, c - 1))}
              onNext={() => setCurrentDay((c) => Math.min(data.totalDays, c + 1))}
              onTransportsChange={(transports) => patchMeta({ transports })}
              sightseeings={sightseeings}
              sightseeingsLoadHint={sightseeingsLoadHint}
            />
          )}
          {view === 'hotels' && (
            <div className={dw.section}>
              <HotelCatalogue
                hotels={data.hotels}
                cities={cities}
                onChange={(hotels) => patchMeta({ hotels })}
              />
            </div>
          )}
          {view === 'transport' && (
            <div className={dw.section}>
              <TransportCatalogue
                transports={data.transports}
                days={data.days}
                onChange={(transports) => patchMeta({ transports })}
              />
            </div>
          )}
          {view === 'booking' && (
            <div className={dw.section}>
              <BookingSimulator
                data={data}
                bookingState={bookingState}
                onBookingChange={setBookingState}
                travellers={travellers}
                onTravellersChange={setTravellers}
              />
            </div>
          )}
          {view === 'tax' && (
            <div className={dw.section}>
              <div className={dw.sectionHead}>
                <div>
                  <h5>Pricing rules</h5>
                  <p>All costs sum selected services and allocated rooms.</p>
                </div>
              </div>
              <Row>
                <Col md={4}>
                  <div className={dw.field}>
                    <label className={dw.fieldLabel}>Markup on supplier cost (%)</label>
                    <Form.Control
                      type="number"
                      value={data.markupPercent}
                      onChange={(e) => patchMeta({ markupPercent: Number(e.target.value) })}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className={dw.field}>
                    <label className={dw.fieldLabel}>GST rate (%)</label>
                    <Form.Control
                      type="number"
                      value={data.gstRate}
                      onChange={(e) => patchMeta({ gstRate: Number(e.target.value) })}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className={dw.field}>
                    <label className={dw.fieldLabel}>GST entry mode</label>
                    <Form.Control
                      as="select"
                      value={data.gstMode}
                      onChange={(e) => patchMeta({ gstMode: e.target.value })}
                    />
                  </div>
                </Col>
              </Row>
              <div className={`${dw.panelNoteWarn} ${dw.foot}`} style={{ marginTop: 12 }}>
                GST treatment depends on booking structure and current law; validate production
                rules with your tax adviser before publishing.
              </div>
            </div>
          )}
          </div>
        </div>

        <aside className={`${dw.card} ${dw.summary}`}>
          <PricingSummary
            data={data}
            travellers={travellers}
            onTravellersChange={setTravellers}
            bookingState={bookingState}
          />
        </aside>
      </div>
    </div>
  );
};

export default DaywiseItineraryEditor;
